/**
 * Per-session reducer wrapper around the epi-chat slice.
 *
 * A single socket connection carries events for multiple epi-study-agent
 * sessions concurrently — the backend keeps in-flight runs alive when the
 * user swaps tabs, and a new session's welcome stream keeps firing even
 * after the user has focused a different session. So we maintain one
 * ``EpiChatState`` per session id in a map, and route each inbound action
 * to the session it belongs to. The UI reads whichever slot matches the
 * currently-focused ``activeSessionId``.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import {
  epiChatActions,
  epiChatReducer,
  initialEpiChatState,
  type EpiChatAction,
  type EpiChatState,
} from '../state/epiChatSlice';

export type EpiChatStateBySession = Record<string, EpiChatState>;

export interface UseEpiChatStateResult {
  stateBySession: EpiChatStateBySession;
  dispatchTo: (sessionId: string, action: EpiChatAction) => void;
  /** Drop a session's state from the in-memory map (e.g. on delete). */
  dropSession: (sessionId: string) => void;
  actions: typeof epiChatActions;
}

export const useEpiChatState = (): UseEpiChatStateResult => {
  const [stateBySession, setStateBySession] = useState<EpiChatStateBySession>({});
  // Tombstone for dropped session ids. After a session is explicitly dropped
  // (either auto-deleted as empty or removed from the sidebar) we keep its id
  // here so stragglers — e.g. the tail of a welcome stream whose cancel
  // hasn't yet propagated through the socket — can't resurrect it.
  const tombstonesRef = useRef<Set<string>>(new Set());

  const dispatchTo = useCallback((sessionId: string, action: EpiChatAction) => {
    if (tombstonesRef.current.has(sessionId)) return;
    setStateBySession((prev) => {
      const current = prev[sessionId] ?? initialEpiChatState;
      const next = epiChatReducer(current, action);
      if (next === current) return prev;
      return { ...prev, [sessionId]: next };
    });
  }, []);

  const dropSession = useCallback((sessionId: string) => {
    tombstonesRef.current.add(sessionId);
    setStateBySession((prev) => {
      if (!(sessionId in prev)) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
  }, []);

  return useMemo(
    () => ({ stateBySession, dispatchTo, dropSession, actions: epiChatActions }),
    [stateBySession, dispatchTo, dropSession]
  );
};

export const selectSessionState = (
  stateBySession: EpiChatStateBySession,
  sessionId: string | null
): EpiChatState =>
  sessionId ? stateBySession[sessionId] ?? initialEpiChatState : initialEpiChatState;
